import { useState } from 'react';
import { Flex, Text, Input, IconButton, FormControl } from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import Rating from 'react-rating';
import { TitleText } from 'components';
import StartEmpty from 'assets/images/star-empty.png';
import StartFull from 'assets/images/star-full.png';
import { useAuthContext } from 'context/AuthProvider';

const RateInterviewFormValidation = Yup.object().shape({
  speedRating: Yup.number().nullable(true).required('Please provide a rating'),
  speedComment: Yup.string(),
  politenessRating: Yup.number()
    .nullable(true)
    .required('Please provide a rating'),
  politenessComment: Yup.string(),
  clarityRating: Yup.number()
    .nullable(true)
    .required('Please provide a rating'),
  clarityComment: Yup.string(),
  answerQuestion: Yup.bool()
    .nullable(true)
    .required('Please provide a response'),
  costsCover: Yup.bool().nullable(true).required('Please provide a response'),
});

type InitialValues = {
  speedRating: number;
  speedComment: string;
  politenessRating: number;
  politenessComment: string;
  clarityRating: number;
  clarityComment: string;
  answerQuestion?: boolean;
  costsCover?: boolean;
};

const emStyle = {
  fontWeight: 700,
  fontStyle: 'normal',
};

const RateInterview = (): JSX.Element => {
  const [step, setStep] = useState(1);
  const {
    limit = 5,
    updateUnlockJobDetails,
    updateJobViewDetails,
  } = useAuthContext();

  const nextStep = () => {
    const newVal = step + 1;
    setStep(newVal);
  };

  return (
    <Flex
      direction="column"
      justifyContent="center"
      alignItems="center"
      height="100%"
      pointerEvents="all"
    >
      <TitleText
        fontWeight={700}
        color="custom.levoBlack"
        text="Want to see more?"
        mb={5}
      />
      <Text
        fontFamily="Lato"
        fontSize="20px"
        fontWeight={500}
        lineHeight="24px"
        textAlign="center"
      >
        {step === 1 && (
          <>
            How quickly did
            <em style={emStyle}> Compassion Home Care </em>
            get back to you?
          </>
        )}
        {step === 2 && (
          <>
            How polite were the recruiters at
            <em style={emStyle}> Compassion Home Care</em>?
          </>
        )}
        {step === 3 && (
          <>
            How clear was the interview process at
            <em style={emStyle}> Compassion Home Care</em>?
          </>
        )}
        {step === 4 && (
          <>
            Did
            <em style={emStyle}> Compassion Home Care </em>
            answer your questions clearly?
          </>
        )}
        {step === 5 && (
          <>
            Did
            <em style={emStyle}> Compassion Home Care </em>
            cover the costs of application? (e.g. TB tests, background checks)
          </>
        )}
      </Text>
      <Formik
        validationSchema={RateInterviewFormValidation}
        initialValues={{
          speedRating: 0,
          speedComment: '',
          politenessRating: 0,
          politenessComment: '',
          clarityRating: 0,
          clarityComment: '',
        }}
        onSubmit={async (values) => {
          updateUnlockJobDetails &&
            updateUnlockJobDetails({ ratedInterview: true });
          updateJobViewDetails && updateJobViewDetails({ newLimit: limit + 2 });
        }}
      >
        {({
          values,
          isValid,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }: FormikProps<InitialValues>) => (
          <Form
            style={{
              width: '100%',
              marginTop: '24px',
            }}
          >
            {step === 1 && (
              <Flex
                direction="column"
                width="100%"
                justifyContent="center"
                alignItems="center"
              >
                <Flex
                  direction="column"
                  width="100%"
                  alignItems="center"
                  borderRadius="8px"
                  padding={6}
                  bg="#FFFFFF"
                >
                  <Text color="custom.levoBlue" textAlign="center">
                    Select your rating
                  </Text>
                  <FormControl
                    id="speedRating"
                    display="flex"
                    justifyContent="center"
                    my={4}
                  >
                    <Rating
                      initialRating={values.speedRating}
                      emptySymbol={
                        <img src={StartEmpty} alt="empty" className="icon" />
                      }
                      fullSymbol={
                        <img src={StartFull} alt="empty" className="icon" />
                      }
                      onChange={(value) => setFieldValue('speedRating', value)}
                    />
                  </FormControl>
                  <FormControl id="speedComment">
                    <Input
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.speedComment}
                      borderRadius="8px"
                      bg="#EFEFEF"
                      placeholder="Write a comment"
                    />
                  </FormControl>
                </Flex>
                <Flex mt={8} justifyContent="flex-end">
                  <IconButton
                    variant="outline"
                    colorScheme="blackAlpha"
                    aria-label="Next Step"
                    icon={<ArrowForwardIcon boxSize={6} color="#212121" />}
                    isRound
                    borderWidth="2px"
                    borderColor="#212121"
                    onClick={() => nextStep()}
                  />
                </Flex>
              </Flex>
            )}
            {step === 2 && (
              <Flex
                direction="column"
                width="100%"
                justifyContent="center"
                alignItems="center"
              >
                <Flex
                  direction="column"
                  width="100%"
                  alignItems="center"
                  borderRadius="8px"
                  padding={6}
                  bg="#FFFFFF"
                >
                  <Text color="custom.levoBlue" textAlign="center">
                    Select your rating
                  </Text>
                  <FormControl
                    id="speedRating"
                    display="flex"
                    justifyContent="center"
                    my={4}
                  >
                    <Rating
                      initialRating={values.politenessRating}
                      emptySymbol={
                        <img src={StartEmpty} alt="empty" className="icon" />
                      }
                      fullSymbol={
                        <img src={StartFull} alt="empty" className="icon" />
                      }
                      onChange={(value) =>
                        setFieldValue('politenessRating', value)
                      }
                    />
                  </FormControl>
                  <FormControl id="politenessComment">
                    <Input
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.politenessComment}
                      borderRadius="8px"
                      bg="#EFEFEF"
                      placeholder="Write a comment"
                    />
                  </FormControl>
                </Flex>
                <Flex mt={8} justifyContent="flex-end">
                  <IconButton
                    variant="outline"
                    colorScheme="blackAlpha"
                    aria-label="Next Step"
                    icon={<ArrowForwardIcon boxSize={6} color="#212121" />}
                    isRound
                    borderWidth="2px"
                    borderColor="#212121"
                    onClick={() => nextStep()}
                  />
                </Flex>
              </Flex>
            )}
            {step === 3 && (
              <Flex
                direction="column"
                width="100%"
                justifyContent="center"
                alignItems="center"
              >
                <Flex
                  direction="column"
                  width="100%"
                  alignItems="center"
                  borderRadius="8px"
                  padding={6}
                  bg="#FFFFFF"
                >
                  <Text color="custom.levoBlue" textAlign="center">
                    Select your rating
                  </Text>
                  <FormControl
                    id="clarityRating"
                    display="flex"
                    justifyContent="center"
                    my={4}
                  >
                    <Rating
                      initialRating={values.clarityRating}
                      emptySymbol={
                        <img src={StartEmpty} alt="empty" className="icon" />
                      }
                      fullSymbol={
                        <img src={StartFull} alt="empty" className="icon" />
                      }
                      onChange={(value) =>
                        setFieldValue('clarityRating', value)
                      }
                    />
                  </FormControl>
                  <FormControl id="clarityComment">
                    <Input
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.clarityComment}
                      borderRadius="8px"
                      bg="#EFEFEF"
                      placeholder="Write a comment"
                    />
                  </FormControl>
                </Flex>
                <Flex mt={8} justifyContent="flex-end">
                  <IconButton
                    variant="outline"
                    colorScheme="blackAlpha"
                    aria-label="Next Step"
                    icon={<ArrowForwardIcon boxSize={6} color="#212121" />}
                    isRound
                    borderWidth="2px"
                    borderColor="#212121"
                    onClick={() => nextStep()}
                  />
                </Flex>
              </Flex>
            )}
            {step === 4 && (
              <Flex
                direction="column"
                width="100%"
                justifyContent="center"
                alignItems="center"
              >
                <Flex
                  role="button"
                  aria-label="yes"
                  direction="column"
                  width="100%"
                  alignItems="center"
                  borderRadius="8px"
                  padding={6}
                  bg={
                    values.answerQuestion
                      ? 'custom.levoBlue'
                      : 'custom.levoWhite'
                  }
                  cursor="pointer"
                  onClick={() => setFieldValue('answerQuestion', true)}
                >
                  <Text
                    color={
                      values.answerQuestion
                        ? 'custom.levoWhite'
                        : 'custom.levoBlue'
                    }
                    textAlign="center"
                  >
                    Yes
                  </Text>
                </Flex>
                <Flex
                  role="button"
                  aria-label="yes"
                  direction="column"
                  width="100%"
                  alignItems="center"
                  borderRadius="8px"
                  padding={6}
                  bg={
                    values.answerQuestion === false
                      ? 'custom.levoBlue'
                      : 'custom.levoWhite'
                  }
                  mt={8}
                  cursor="pointer"
                  onClick={() => setFieldValue('answerQuestion', false)}
                >
                  <Text
                    color={
                      values.answerQuestion === false
                        ? 'custom.levoWhite'
                        : 'custom.levoBlue'
                    }
                    textAlign="center"
                  >
                    No
                  </Text>
                </Flex>
                <Flex mt={8} justifyContent="flex-end">
                  <IconButton
                    variant="outline"
                    colorScheme="blackAlpha"
                    aria-label="Next Step"
                    icon={<ArrowForwardIcon boxSize={6} color="#212121" />}
                    isRound
                    borderWidth="2px"
                    borderColor="#212121"
                    disabled={values.answerQuestion === undefined}
                    onClick={() => nextStep()}
                  />
                </Flex>
              </Flex>
            )}
            {step === 5 && (
              <Flex
                direction="column"
                width="100%"
                justifyContent="center"
                alignItems="center"
              >
                <Flex
                  role="button"
                  aria-label="yes"
                  direction="column"
                  width="100%"
                  alignItems="center"
                  borderRadius="8px"
                  padding={6}
                  bg={
                    values.costsCover ? 'custom.levoBlue' : 'custom.levoWhite'
                  }
                  cursor="pointer"
                  onClick={() => setFieldValue('costsCover', true)}
                >
                  <Text
                    color={
                      values.costsCover ? 'custom.levoWhite' : 'custom.levoBlue'
                    }
                    textAlign="center"
                  >
                    Yes
                  </Text>
                </Flex>
                <Flex
                  role="button"
                  aria-label="no"
                  direction="column"
                  width="100%"
                  alignItems="center"
                  borderRadius="8px"
                  padding={6}
                  bg={
                    values.costsCover === false
                      ? 'custom.levoBlue'
                      : 'custom.levoWhite'
                  }
                  mt={8}
                  cursor="pointer"
                  onClick={() => setFieldValue('costsCover', false)}
                >
                  <Text
                    color={
                      values.costsCover === false
                        ? 'custom.levoWhite'
                        : 'custom.levoBlue'
                    }
                    textAlign="center"
                  >
                    No
                  </Text>
                </Flex>
                <Flex mt={8} justifyContent="flex-end">
                  <IconButton
                    variant="outline"
                    colorScheme="blackAlpha"
                    aria-label="Next Step"
                    icon={<ArrowForwardIcon boxSize={6} color="#212121" />}
                    disabled={!isValid}
                    isRound
                    borderWidth="2px"
                    borderColor="#212121"
                    onClick={() => handleSubmit()}
                  />
                </Flex>
              </Flex>
            )}
          </Form>
        )}
      </Formik>
    </Flex>
  );
};

export default RateInterview;
